import React from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, Divider, Skeleton, TextField, Stack, Button, Snackbar, Alert, InputAdornment, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import TodayIcon from '@mui/icons-material/Today';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { getPendingInvoices, sendInvoiceReminder, getApiError } from '../../../utils/api';

function isOverdue(dueDate) {
  if (!dueDate) return false;
  const today = new Date();
  const due = new Date(dueDate);
  return due < today.setHours(0,0,0,0);
}

function isDueToday(dueDate) {
  if (!dueDate) return false;
  const today = new Date();
  const due = new Date(dueDate);
  return due.toDateString() === today.toDateString();
}

export default function BusinessCredits() {
  const [invoices, setInvoices] = React.useState([]);
  const [loadingInv, setLoadingInv] = React.useState(true);
  const [search, setSearch] = React.useState('');
  const [filter, setFilter] = React.useState('all');
  const [snackbar, setSnackbar] = React.useState({ open: false, message: '', severity: 'success' });
  const [reminderDisabled, setReminderDisabled] = React.useState({}); // { [invoiceId]: timestamp }
  const [reminderDialog, setReminderDialog] = React.useState({ open: false, invoice: null });
  const [reminderAmount, setReminderAmount] = React.useState('');
  const [reminderLoading, setReminderLoading] = React.useState(false);

  React.useEffect(() => {
    getPendingInvoices().then(res => {
      // API returns { results: [...], total, page, pageSize }
      setInvoices(res.data.results || []);
    }).finally(() => setLoadingInv(false));
  }, []);

  const searchLower = search.toLowerCase();
  // Pending Invoices (use API data, correct field names)
  const filteredInvoices = invoices.filter(inv => {
    if (!inv.remainingAmount || Number(inv.remainingAmount) <= 0) return false;
    // Search by customer, invoice number, or due date
    if (searchLower && !(
      (inv.invoiceId && inv.invoiceId.toString().toLowerCase().includes(searchLower)) ||
      (inv.customerNumber && inv.customerNumber.toString().toLowerCase().includes(searchLower)) ||
      (inv.date && inv.date.toLowerCase().includes(searchLower))
    )) return false;
    // Filter chips
    if (filter === 'overdue' && !isOverdue(inv.date)) return false;
    if (filter === 'dueToday' && !isDueToday(inv.date)) return false;
    return true;
  });

  const handleSendReminder = (inv) => {
    setReminderDialog({ open: true, invoice: inv });
    setReminderAmount(inv.remainingAmount);
  };

  const handleReminderDialogClose = () => {
    setReminderDialog({ open: false, invoice: null });
    setReminderAmount('');
  };

  const handleReminderSend = async (useEditedAmount = false) => {
    if (!reminderDialog.invoice) return;
    setReminderLoading(true);
    try {
      const body = useEditedAmount ? { amount: reminderAmount } : {};
      const res = await sendInvoiceReminder(reminderDialog.invoice.invoiceId, body);
      setSnackbar({ open: true, message: res.data.message || 'Reminder sent successfully.', severity: 'success' });
      // Disable button for 15 minutes
      setReminderDisabled(prev => ({ ...prev, [reminderDialog.invoice.invoiceId]: Date.now() + 900000 }));
      setTimeout(() => {
        setReminderDisabled(prev => {
          const updated = { ...prev };
          delete updated[reminderDialog.invoice.invoiceId];
          return updated;
        });
      }, 900000);
      handleReminderDialogClose();
    } catch (err) {
      setSnackbar({ open: true, message: getApiError(err), severity: 'error' });
    } finally {
      setReminderLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', display: 'flex', alignItems: 'top', justifyContent: 'center', py: { xs: 2, md: 6 }, px: { xs: 1, md: 0 } }}>
      <Paper sx={{ p: { xs: 2, md: 4 }, maxWidth: 1100, width: '100%', borderRadius: 6, boxShadow: 0, border: '1px solid', borderColor: 'divider', mx: 'auto', mb: 4, background: 'background.paper' }}>
        <Typography variant="h4" fontWeight={700} color="text.primary" mb={1}>
          Pending Invoice Payments
        </Typography>
        <Divider sx={{ mb: 3 }} />
        {/* Filter/Search Bar */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={2}>
          <TextField
            variant="outlined"
            size="small"
            placeholder="Search by invoice number, due date, or mobile number"
            value={search}
            onChange={e => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="primary" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 220, flex: 1 }}
          />
          <Stack direction="row" spacing={1}>
            <Chip label="All" color={filter === 'all' ? 'primary' : 'default'} onClick={() => setFilter('all')} clickable />
            <Chip label="Due Today" icon={<TodayIcon />} color={filter === 'dueToday' ? 'primary' : 'default'} onClick={() => setFilter('dueToday')} clickable />
            <Chip label="Overdue" icon={<WarningAmberIcon />} color={filter === 'overdue' ? 'error' : 'default'} onClick={() => setFilter('overdue')} clickable />
          </Stack>
        </Stack>
        {loadingInv ? (
          <Skeleton variant="rectangular" height={220} />
        ) : (
          <TableContainer>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>Invoice ID</TableCell>
                  <TableCell sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>Customer</TableCell>
                  <TableCell sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>Amount</TableCell>
                  <TableCell sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>Due Date</TableCell>
                  <TableCell sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>Status</TableCell>
                  <TableCell sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredInvoices.length === 0 ? (
                  <TableRow><TableCell colSpan={9} align="center">No pending invoices found.</TableCell></TableRow>
                ) : filteredInvoices.map((inv, idx) => {
                  const overdue = isOverdue(inv.date);
                  const dueToday = isDueToday(inv.date);
                  const paidAmount = Number(inv.totalAmount) - Number(inv.remainingAmount);
                  return (
                    <TableRow key={inv.invoiceId} hover>
                      <TableCell>{inv.invoiceId}</TableCell>
                      <TableCell>{inv.customerNumber}</TableCell>
                      <TableCell>₹{Number(inv.totalAmount).toLocaleString()}</TableCell>
                      <TableCell>{inv.date ? new Date(inv.date).toLocaleDateString() : 'N/A'}</TableCell>
                      <TableCell>
                        {inv.status ? (
                          <Chip label={inv.status.charAt(0).toUpperCase() + inv.status.slice(1)} color={inv.status === 'pending' ? 'info' : inv.status === 'overdue' ? 'error' : 'warning'} size="small" />
                        ) : (
                          <Chip label="Pending" color="info" size="small" />
                        )}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="contained"
                          color={overdue ? 'error' : 'primary'}
                          size="small"
                          onClick={() => handleSendReminder(inv)}
                          disabled={!!reminderDisabled[inv.invoiceId] && reminderDisabled[inv.invoiceId] > Date.now()}
                        >
                          {reminderDisabled[inv.invoiceId] && reminderDisabled[inv.invoiceId] > Date.now() ? 'Wait 15 min' : 'Send Reminder'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={2500}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert severity={snackbar.severity} variant="filled" sx={{ fontWeight: 600 }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
        {/* Reminder Dialog */}
        <Dialog open={reminderDialog.open} onClose={handleReminderDialogClose}>
          <DialogTitle>Send Payment Reminder</DialogTitle>
          <DialogContent>
            <Typography gutterBottom>Customer: <b>{reminderDialog.invoice?.customerNumber}</b></Typography>
            <Typography gutterBottom>Invoice ID: <b>{reminderDialog.invoice?.invoiceId}</b></Typography>
            <Typography gutterBottom>Current Remaining Amount: <b>₹{reminderDialog.invoice?.remainingAmount}</b></Typography>
            <TextField
              label="Edit Amount (optional)"
              type="number"
              fullWidth
              margin="normal"
              value={reminderAmount}
              onChange={e => setReminderAmount(e.target.value)}
              inputProps={{ min: 0 }}
            />
            <Typography variant="caption" color="text.secondary">Leave unchanged to send the current amount.</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleReminderDialogClose} disabled={reminderLoading}>Cancel</Button>
            <Button
              onClick={() => handleReminderSend(false)}
              disabled={reminderLoading}
              variant="contained"
              color="primary"
            >
              {reminderLoading ? <CircularProgress size={20} /> : 'Send as is'}
            </Button>
            <Button
              onClick={() => handleReminderSend(true)}
              disabled={reminderLoading || !reminderAmount}
              variant="contained"
              color="success"
            >
              {reminderLoading ? <CircularProgress size={20} /> : 'Send with Edited Amount'}
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </Box>
  );
} 
